import React, { useState } from 'react';
import { Row } from 'react-bootstrap';
import {
  Controller,
  useFormContext,
  ControllerRenderProps,
} from 'react-hook-form';
import {
  USCGResourceKindOptions,
  TypeOptions,
} from 'components/IncidentConfig/ResourceTypes';
import {
  ResourceClassification,
  useCustomResources,
} from './hooks/customResourceTypeHooks';
import StylishNewComboBox, {
  ComboBoxOption,
} from 'components/DesignSystems/New/StylishNewComboBox';

const ResourceSelectFields: React.FC<{
  disabled?: boolean;
  labels?: boolean;
  disableKind?: string;
}> = ({ disabled, labels, disableKind }) => {
  const { control, setValue, watch } = useFormContext();

  const { data: customKinds = [] } = useCustomResources(
    ResourceClassification.Kind
  );
  const { data: customTypes = [] } = useCustomResources(
    ResourceClassification.Type
  );

  const [pendingCustomKinds, setPendingCustomKinds] = useState<
    ComboBoxOption[]
  >([]);
  const [pendingCustomTypes, setPendingCustomTypes] = useState<
    ComboBoxOption[]
  >([]);

  const kindOptions: ComboBoxOption[] = [
    ...USCGResourceKindOptions.filter((option) => option.value !== disableKind),
    ...pendingCustomKinds,
    ...customKinds.map((resource) => ({
      value: resource.name!,
      label: `${resource.name} (Custom)`,
    })),
  ];

  const typeOptions: ComboBoxOption[] = [
    ...TypeOptions,
    ...pendingCustomTypes,
    ...customTypes.map((resource) => ({
      value: resource.type!,
      label: `${resource.type} (Custom)`,
    })),
  ];

  const handleKindChange = (
    field: ControllerRenderProps,
    option: ComboBoxOption | null
  ) => {
    const selectedValue = option?.value;

    if (
      selectedValue &&
      !kindOptions.find((opt) => opt.value === selectedValue)
    ) {
      setPendingCustomKinds((prev) => [
        ...prev,
        {
          value: selectedValue,
          label: `${selectedValue} (Custom)`,
        },
      ]);
    }

    field.onChange(selectedValue);
    setValue('kind', selectedValue);
  };

  const handleTypeChange = (
    field: ControllerRenderProps,
    option: ComboBoxOption | null
  ) => {
    const selectedValue = option?.value;

    if (
      selectedValue &&
      !typeOptions.find((opt) => opt.value === selectedValue)
    ) {
      setPendingCustomTypes((prev) => [
        ...prev,
        {
          value: selectedValue,
          label: `${selectedValue} (Custom)`,
        },
      ]);
    }

    field.onChange(selectedValue || '');
  };

  return (
    <Row>
      <div className="col-6 mb-3">
        {labels && <label className="form-label">Resource Kind *</label>}
        <Controller
          name="kind"
          control={control}
          rules={{ required: 'Resource kind is required' }}
          render={({ field }) => (
            <StylishNewComboBox
              options={kindOptions}
              value={
                kindOptions.find((option) => option.value === field.value) ||
                (field.value
                  ? { value: field.value, label: field.value }
                  : null)
              }
              onChange={(option) =>
                handleKindChange(field, option as ComboBoxOption)
              }
              placeholder="Select a resource kind..."
              allowCustomValue={true}
              customOptionLabel="+ Add Custom Kind"
              isDisabled={disabled}
            />
          )}
        />
      </div>
      <div className="col-6 mb-3">
        {labels && <label className="form-label">Type *</label>}
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <StylishNewComboBox
              options={typeOptions}
              value={
                typeOptions.find((option) => option.value === field.value) ||
                (field.value
                  ? { value: field.value, label: field.value }
                  : null)
              }
              isDisabled={!watch('kind') || disabled}
              onChange={(option) =>
                handleTypeChange(field, option as ComboBoxOption)
              }
              placeholder="Select a resource type..."
              allowCustomValue={true}
              customOptionLabel="+ Add Custom Type"
            />
          )}
        />
      </div>
    </Row>
  );
};

export default ResourceSelectFields;
